import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { Control, useForm } from 'react-hook-form';

import {
  AssetDefaultOrSelectedTypeMap,
  PublicOfferAssetToInvestNormalized,
  PublicOfferDetailsModalityWithTimelineNormalized,
} from '../../../../../../../store/product/types';

import {
  PublicOfferFormDataT2,
  getRemunerationDescription,
  getValidationCreateSchemaT2,
} from './constants';

export const usePublicOfferAssetRemunerationController = (
  offer: AssetDefaultOrSelectedTypeMap['publicOffers'],
  _: PublicOfferAssetToInvestNormalized,
  modality: PublicOfferDetailsModalityWithTimelineNormalized
) => {
  const minimumValue = modality.minimumApplication;
  const maximumValue = modality.maximumApplication;

  const minimumRemuneration = offer.minimumRemuneration;
  const maximumRemuneration = offer.maximumRemuneration;

  const [isValueFocused, setIsValueFocused] = useState(false);
  const [isRemunerationFocused, setIsRemunerationFocused] = useState(false);
  const [valueApplied, setValueApplied] = useState(minimumValue);
  const [remunerationApplied, setRemunerationApplied] = useState(minimumRemuneration);

  const valueAppliedFormatted = Utils.Mask.Money(valueApplied);
  const minValueFormatted = Utils.Mask.Money(minimumValue);
  const minRemunerationFormatted = Utils.Mask.Percentage(minimumRemuneration);
  const remunerationAppliedFormatted = Utils.Mask.Percentage(remunerationApplied);

  const tableForm = useForm<PublicOfferFormDataT2>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      priceItem: minValueFormatted,
      remuneration: minRemunerationFormatted,
    },
    resolver: zodResolver(
      getValidationCreateSchemaT2(
        minimumValue,
        maximumValue,
        minimumRemuneration,
        maximumRemuneration
      )
    ),
  });

  const handleInputValueFocus = () => {
    setIsValueFocused(true);
    if (tableForm.formState.errors?.priceItem) {
      tableForm.resetField('priceItem');
    }
  };

  const handleInputRemunerationFocus = () => {
    setIsRemunerationFocused(true);
    if (tableForm.formState.errors?.remuneration) {
      tableForm.resetField('remuneration');
    }
  };

  const handleInputValueClear = () => {
    setIsValueFocused(false);
    tableForm.resetField('priceItem');
    tableForm.clearErrors('priceItem');
  };

  const handleInputRemunerationClear = () => {
    setIsRemunerationFocused(false);
    tableForm.resetField('remuneration');
    tableForm.clearErrors('remuneration');
  };

  const handleInputValueBlur = async () => {
    const currentValue = tableForm.getValues('priceItem');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    if (currentValueUnformatted >= minimumValue && currentValueUnformatted <= maximumValue) {
      tableForm.setValue('priceItem', currentValue);
      setValueApplied(currentValueUnformatted);
    } else {
      tableForm.clearErrors('priceItem');
      tableForm.setValue('priceItem', Utils.Mask.Money(minimumValue));
      setValueApplied(minimumValue);
    }

    if (isValueFocused) {
      setIsValueFocused(false);
    }
  };

  const handleInputRemunerationBlur = async () => {
    const currentRemuneration = tableForm.getValues('remuneration');
    const currentRemunerationUnformatted = Utils.Unmask.Percentage(currentRemuneration);

    if (
      currentRemunerationUnformatted >= minimumRemuneration &&
      currentRemunerationUnformatted <= maximumRemuneration
    ) {
      tableForm.setValue('remuneration', currentRemuneration);
      setRemunerationApplied(currentRemunerationUnformatted);
    } else {
      tableForm.clearErrors('remuneration');
      tableForm.setValue('remuneration', Utils.Mask.Percentage(minimumRemuneration));
      setRemunerationApplied(minimumRemuneration);
    }

    if (isRemunerationFocused) {
      setIsRemunerationFocused(false);
    }
  };

  const handleSubmit = async ({ priceItem }: PublicOfferFormDataT2) => {
    const priceItemUnformatted = Utils.Unmask.Money(priceItem);
    tableForm.setValue('priceItem', priceItem);
    setValueApplied(priceItemUnformatted);

    setIsValueFocused(false);
  };

  const minMaxValueDescription = useMemo(() => getRemunerationDescription(offer), [offer]);

  return {
    minimumValue,
    minimumRemuneration,
    handleInputValueFocus,
    handleInputRemunerationFocus,
    handleInputRemunerationClear,
    handleInputValueClear,
    isValueFocused,
    handleInputValueBlur,
    handleInputRemunerationBlur,
    isRemunerationFocused,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    hasErrorValue: tableForm.formState.errors?.priceItem,
    hasErrorRemuneration: tableForm.formState.errors?.remuneration,
    currentValue: valueApplied,
    currentValueFormatted: valueAppliedFormatted,
    currentRemuneration: remunerationApplied,
    currentRemunerationFormatted: remunerationAppliedFormatted,
    minMaxValueDescription,
  };
};

export type PublicOfferMinimumRemunerationController = ReturnType<
  typeof usePublicOfferAssetRemunerationController
>;
